'use client';
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, post, normalize } from '@curio/client';
import { reviewQueueSchema, type Account } from '@curio/contracts';

export function reviewQueuePath(excludeProductId?: string) {
  return excludeProductId
    ? `/review-queue?exclude=${encodeURIComponent(excludeProductId)}`
    : '/review-queue';
}

export function useReviewQueueRotation(
  account: Account,
  displayedProductIds: string[],
  excludeProductId?: string,
) {
  const queryKey = excludeProductId
    ? ['review-queue', account.id, excludeProductId]
    : ['review-queue', account.id];
  const client = useQueryClient();
  const operation = useRef<{
    action: 'skip' | 'reset';
    productIds: string[];
    idempotencyKey: string;
  } | null>(null);
  const different = useMutation({
    mutationFn: async (action: 'skip' | 'reset') => {
      // A lost response must not make a retry skip another batch or reset new skips.
      if (!operation.current || operation.current.action !== action)
        operation.current = {
          action,
          productIds: displayedProductIds,
          idempotencyKey: crypto.randomUUID(),
        };
      const { productIds, idempotencyKey } = operation.current;
      await client.cancelQueries({ queryKey: ['review-queue', account.id] });
      await post(`/review-queue/${action}`, { productIds, idempotencyKey });
      return api(reviewQueuePath(excludeProductId), {}, reviewQueueSchema);
    },
    onSuccess: async (data) => {
      await client.cancelQueries({ queryKey: ['review-queue', account.id] });
      normalize(client, data.products);
      client.setQueryData(queryKey, data);
      await client.invalidateQueries({
        queryKey: ['review-queue', account.id],
        predicate: (query) => query.queryKey[2] !== excludeProductId,
      });
      operation.current = null;
    },
  });
  return different;
}
